import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

export default function FunderSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',
    address: '',
    phone: '',
    email: '',
  });

  useEffect(() => {
    const fetchFunderProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        const response = await axios.get(`${API_URL}/settings/funder`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setFormData({
          name: response.data.name,
          abbreviation: response.data.abbreviation,
          address: response.data.address,
          phone: response.data.phone,
          email: response.data.email,
        });
      } catch (err) {
        setError('Failed to load funder profile');
        if (err.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFunderProfile();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    setError('');
    setSuccess('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/settings/funder`, formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccess('Funder profile updated successfully');
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to update profile';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Link to="/dashboard" style={styles.backLink}>← Back to Dashboard</Link>
        <h1 style={styles.title}>Funder Settings</h1>
      </div>

      <div style={styles.content}>
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Company Information</h2>
          <p style={styles.cardDesc}>Update your funder/company details</p>

          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.success}>{success}</div>}

          <form onSubmit={handleSave} style={styles.form}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Company Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Your company name"
                style={styles.input}
                disabled={saving}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Abbreviation</label>
              <input
                type="text"
                name="abbreviation"
                value={formData.abbreviation}
                onChange={handleChange}
                placeholder="e.g., MCG"
                style={styles.input}
                disabled={saving}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Address</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Business address"
                style={styles.input}
                disabled={saving}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Phone number"
                style={styles.input}
                disabled={saving}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="company@example.com"
                style={styles.input}
                disabled={saving}
              />
            </div>

            <button
              type="submit"
              style={{
                ...styles.submitBtn,
                opacity: saving ? 0.6 : 1,
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>

        <div style={styles.infoCard}>
          <h3 style={styles.infoTitle}>About Phase 1</h3>
          <p style={styles.infoText}>
            This settings page allows you to configure your funder/company profile.
            Template configuration and other advanced settings will be added in future phases.
          </p>
          <p style={styles.infoText}>
            <strong>Current Phase 1 Features:</strong>
          </p>
          <ul style={styles.infoList}>
            <li>Tenant + Auth Foundation</li>
            <li>Funder Profile Management</li>
            <li>Disclosure PDF Generation (with auth)</li>
            <li>Calculator Excel Generation (with auth)</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f5f5f5',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
  },
  header: {
    maxWidth: '900px',
    margin: '0 auto 30px',
  },
  backLink: {
    color: '#667eea',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: '12px 0 0 0',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '20px',
  },
  card: {
    background: 'white',
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  infoCard: {
    background: '#f0f4ff',
    padding: '20px',
    borderRadius: '8px',
    borderLeft: '4px solid #667eea',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 4px 0',
    color: '#333',
  },
  cardDesc: {
    color: '#666',
    fontSize: '14px',
    margin: '0 0 20px 0',
  },
  infoTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 12px 0',
  },
  infoText: {
    color: '#555',
    fontSize: '13px',
    lineHeight: '1.6',
    margin: '0 0 12px 0',
  },
  infoList: {
    paddingLeft: '20px',
    color: '#555',
    fontSize: '13px',
  },
  error: {
    background: '#fee',
    border: '1px solid #fcc',
    color: '#c33',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  success: {
    background: '#efe',
    border: '1px solid #cfc',
    color: '#3c3',
    padding: '12px',
    borderRadius: '4px',
    marginBottom: '16px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#333',
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'Arial, sans-serif',
    boxSizing: 'border-box',
  },
  submitBtn: {
    padding: '12px',
    background: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: 'bold',
    marginTop: '8px',
  },
};
